import './processPolyfill';
import './index.scss';
import './helpers/i18n';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { Loader } from '@spotted-zebra-uk/ui-components';
import App from './App';
import {
  isPublicBookingPath,
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from './helpers/bookingTokenRedaction';
import { setDocumentHeadLinks } from './helpers/setDocumentHeadLinks';
import { MaintenanceProvider } from './components/features/featureFlag/MaintenanceProvider';
import { FeatureFlagsProvider } from './components/features/featureFlag/FeatureFlagsProvider';

setDocumentHeadLinks();

// The public booking route carries the candidate's booking token in the URL.
// Every URL Sentry would send is scrubbed, and session replay is not started
// there at all: a replay records the address bar and the whole page, and the
// candidate has no session to replay in the first place.
const isPublicBookingRoute = isPublicBookingPath(window.location.pathname);

import.meta.env.MODE === 'production' &&
  Sentry.init({
    // Optionally, configure source map handling:
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(isPublicBookingRoute
        ? []
        : [
            Sentry.replayIntegration({
              maskAllInputs: false,
              maskAllText: false,
            }),
          ]),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: isPublicBookingRoute ? 0 : 0.01,
    replaysOnErrorSampleRate: isPublicBookingRoute ? 0 : 0.5,
    release: 'sz-candidate-client@2.67.0',
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
  });

const container = document.getElementById('app');
const root = createRoot(container as HTMLElement);
root.render(
  <Suspense fallback={<Loader variant="bubbles" />}>
    <FeatureFlagsProvider>
      <MaintenanceProvider>
        <App />
      </MaintenanceProvider>
    </FeatureFlagsProvider>
  </Suspense>
);
